import { handleLogout } from "../config/axios";
import decryptData from "../decrypt";
import { apiFetch } from "../config/fetch";

export type NacStatus =
  | "BOZZA"
  | "DA CONFERMARE"
  | "INVIATA"
  | "IN LAVORAZIONE"
  | "LAVORATA"
  | "ARCHIVIATA";

export const NAC_STATUSES: NacStatus[] = [
  "BOZZA",
  "DA CONFERMARE",
  "INVIATA",
  "IN LAVORAZIONE",
  "LAVORATA",
  "ARCHIVIATA",
];

export class NacApiError extends Error {
  errors: string[];
  constructor(message: string, errors: string[] = []) {
    super(message);
    this.errors = errors;
  }
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

async function readBody(response: Response) {
  const result = await response.json();
  if (!(result.encryptedData && result.encryptedKey && result.iv)) return result;
  const decrypted = await decryptData(result.encryptedData, result.encryptedKey, result.iv);
  return decrypted ? JSON.parse(decrypted) : null;
}

async function decodeResponse(response: Response, path: string) {
  if (response.status === 401) {
    // Sessione scaduta: logout. Il corpo della richiesta (dati del cliente) non va mai passato al log.
    handleLogout(response, `/nodeapi/nac${path}`, "");
    throw new NacApiError("Sessione scaduta: accedere di nuovo");
  }
  if (response.status !== 200 && response.status !== 201) {
    // Errore applicativo o del servizio: mostra il messaggio del server se leggibile, senza logout né log del corpo.
    const data = await readBody(response).catch(() => null);
    const message = data?.error ?? (Array.isArray(data?.message) ? data.message[0] : data?.message);
    throw new NacApiError(
      typeof message === "string" && message ? message : `Servizio non disponibile (stato ${response.status})`,
      data?.errors ?? (Array.isArray(data?.message) ? data.message : []),
    );
  }
  const data = await readBody(response);
  if (data && data.error) throw new NacApiError(data.error, data.errors ?? []);
  return data;
}

export async function nacRequest<T = any>(path: string, method = "GET", payload?: unknown): Promise<T> {
  const body = payload === undefined ? undefined : JSON.stringify(payload);
  const response = await apiFetch(`/nodeapi/nac${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body,
  });
  return decodeResponse(response, path);
}

export async function nacUpload<T = any>(id: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(`/nodeapi/nac/${id}/attachments`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return decodeResponse(response, `/${id}/attachments`);
}

export async function nacDownload(id: string, fileId: number, name: string): Promise<void> {
  const response = await apiFetch(`/nodeapi/nac/${id}/attachments/${fileId}`, { headers: authHeaders() });
  if (!response.ok) throw new NacApiError("Download non riuscito");
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Lato cliente (pagina pubblica di conferma): nessun login, nessuna busta cifrata ----

export async function fetchPublicNac(token: string): Promise<any> {
  const response = await fetch(`/nodeapi/nac-public/${encodeURIComponent(token)}`, { cache: "no-store" });
  if (response.status === 404) throw new NacApiError("Link non valido");
  if (!response.ok) throw new NacApiError("Servizio non disponibile, riprovare più tardi");
  return response.json();
}

export async function respondPublicNac(
  token: string,
  body: { outcome: "accept" | "reject"; note?: string; consenso_invio_telematico?: "si" | "no" },
): Promise<void> {
  const response = await fetch(`/nodeapi/nac-public/${encodeURIComponent(token)}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (response.ok) return;
  const data = await response.json().catch(() => null);
  const message = Array.isArray(data?.message) ? data.message[0] : data?.message;
  throw new NacApiError(typeof message === "string" && message ? message : "Operazione non riuscita, riprovare più tardi");
}
